<template>
  <UppyDashboard
    v-if="uppy"
    :props="uppyProps"
    :uppy="uppy"
    data-testid="uppy-dashboard"
  />
</template>

<script setup>
import { Dashboard as UppyDashboard } from '@uppy/vue';
import ResizeObserver from 'resize-observer-polyfill';
import Uppy from '@uppy/core';
import useApi from '@/shared/composables/useApi';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import useUppyUtils from '@/shared/composables/useUppyUtils';
import XHRUpload from '@uppy/xhr-upload';

window.ResizeObserver = ResizeObserver;

const { api } = useApi();
const { locale } = useI18n();
const store = useStore();
const { getUppyLocale, getUploadAPIEndpoint } = useUppyUtils();

const props = defineProps({
  dense: {
    type: Boolean,
    default: false,
  },
  owner: {
    type: Object,
    default: null,
  },
  restrictions: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['uploaded']);

const uppy = ref(null);
const uppyProps = ref({
  proudlyDisplayPoweredByUppy: false,
  inline: true,
  height: props.dense ? 175 : 200,
  locale: getUppyLocale(),
  width: '100%',
});

watch(
  locale,
  (newLocale, formerLocale) => {
    if (newLocale.value && formerLocale.value && formerLocale.value !== newLocale.value) {
      window.location.reload();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (uppy.value) uppy.value.close();
});

onMounted(uppyInit);

function uppyInit() {
  uppy.value = new Uppy({ restrictions: props.restrictions }).use(XHRUpload, {
    endpoint: getUploadAPIEndpoint(),
  });

  uppy.value.on('upload-success', async (file, event) => {
    const params = {
      file: {
        id: event.body.id, // remove the Shrine storage prefix
        storage: 'cache',
        metadata: {
          size: file.size,
          filename: file.name,
          mime_type: file.type,
        },
      },
      business_id: props.owner.business_id,
      child_id: props.owner.child_id,
      group_id: props.owner.group_id,
      owner_type: props.owner.type,
      owner_id: props.owner.id,
      tag: props.owner.tag,
      tags: props.owner.tags || [],
      description: props.owner.description,
    };

    let response;
    if (store.state.role === 'specialist') {
      response = await api.organization.attachment.create(params);
    } else if (store.state.role === 'manager' && props.owner.child_id) {
      response = await api.manager.attachment.create(params);
    } else {
      response = await api.member.attachment.create(params);
    }

    if (response?.status !== 201) return uppy.value.cancelAll();

    emit('uploaded', response.data);
    return true;
  });
}
</script>
